import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth, api } from "../App";
import DashboardLayout from "../components/layout/DashboardLayout";
import VendorQuotationForm from "../components/VendorQuotationForm";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Package, Layers, Target, Clock, Zap,
  FileText, Shield
} from "lucide-react";

const VendorQuotePage = () => {
  const { rfqId } = useParams();
  const { user } = useAuth();
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRFQ = async () => {
      try {
        const res = await api.get(`/rfqs/${rfqId}`);
        setRfq(res.data);
      } catch {
        toast.error("Failed to load RFQ details");
      } finally {
        setLoading(false);
      }
    };
    fetchRFQ();
  }, [rfqId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!rfq) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto py-12 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">RFQ not found</p>
          <Link to="/vendor/matched-rfqs" className="text-orange-600 hover:underline text-sm mt-2 inline-block">
            Back to Matched RFQs
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto py-6 px-4" data-testid="vendor-quote-page">
        {/* Back link */}
        <Link
          to={`/vendor/rfq/${rfqId}`}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-orange-600 mb-6"
          data-testid="back-to-rfq-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to RFQ Details
        </Link>

        {/* RFQ Summary Card */}
        <Card className="mb-6 border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              {rfq.title}
            </CardTitle>
            <p className="text-xs text-slate-400 font-mono">{rfq.rfq_number}</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Material</p>
                <p className="text-slate-700 font-medium mt-0.5 flex items-center gap-1">
                  <Layers className="w-3.5 h-3.5 text-blue-500" /> {rfq.material_type || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Quantity</p>
                <p className="text-slate-700 font-medium mt-0.5 flex items-center gap-1">
                  <Package className="w-3.5 h-3.5 text-amber-500" /> {rfq.quantity} units
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Tolerance</p>
                <p className="text-slate-700 font-medium mt-0.5 flex items-center gap-1">
                  <Target className="w-3.5 h-3.5 text-emerald-500" /> {rfq.tolerance ? `±${rfq.tolerance} mm` : "As per drawing"}
                </p>
              </div>
              {rfq.deadline && (
                <div>
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Deadline</p>
                  <p className="text-slate-700 font-medium mt-0.5 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-red-500" /> {new Date(rfq.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
              )}
            </div>

            {rfq.urgency && rfq.urgency !== "normal" && (
              <div className={`mt-3 px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${
                rfq.urgency === "urgent" ? "bg-red-50 text-red-700" : "bg-orange-50 text-orange-700"
              }`}>
                <Zap className="w-3.5 h-3.5" /> {rfq.urgency === "urgent" ? "Urgent" : "High Priority"}
              </div>
            )}

            {rfq.require_nda && (
              <div className="mt-3 px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 bg-red-50 text-red-700">
                <Shield className="w-3.5 h-3.5" /> NDA Required
              </div>
            )}
          </CardContent>
        </Card>

        {/* Full Quotation Form */}
        <Card className="border-orange-200">
          <CardHeader className="border-b border-orange-100 bg-orange-50/50">
            <CardTitle className="text-lg text-orange-900">Submit Your Quotation</CardTitle>
            <p className="text-sm text-orange-700/70">Fill in your pricing, lead time, and terms below</p>
          </CardHeader>
          <CardContent className="pt-6">
            <VendorQuotationForm
              rfq={rfq}
              onSubmitSuccess={() => {
                toast.success("Quotation submitted successfully!");
                window.close();
              }}
            />
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default VendorQuotePage;
